import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
  LinkedinShareButton,
  LinkedinIcon,
} from "react-share";

import { APPLICATION_STATUS } from "../../config/ApplicationConstants";
import { APP_URL } from "../../config";
import { type RootState } from "../../store";

import PublicNavBar from "./NavBar";
import { downloadBadge } from "./download-utils";

export { default as CertificatePage } from "./Certificate";

/** URI-encodable keyword that indicates a certificate is not yet ready for a given application. */
export const PENDING_SLUG = "pending";

/** The certification approval overview page. */
export default function CertificationApprovalPage() {
  const activeDocumentId = useSelector<RootState>(
    (state) => state.documents.activeDocumentId
  ) as string;
  const documentDetails = useSelector<RootState>(
    (state) => state.documents.byId
  ) as {};
  const activeDocument = documentDetails[activeDocumentId];

  const history = useHistory();
  useEffect(() => {
    if (activeDocument && activeDocument.status !== APPLICATION_STATUS.ACCEPT) {
      history.push("/");
    } else if (!activeDocument) {
      history.push("/onboarding");
    }
  }, [activeDocument, history]);

  const certificateNumber = activeDocument?.certificateData?.certificateNumber;
  return (
    <div className="certificate-page public">
      <PublicNavBar showReferral={true} />

      <div className="section horizontal-page-title-public">
        <div className="block-section container cert-container">
          <div className="block-section-60 font-weight-bold hero">
            <h3 className="heading-3 text-block-2" style={{ fontWeight: 600 }}>
              Congrats!
            </h3>
            <h2 className="heading-3 text-block-2" style={{ fontWeight: 600 }}>
              You are CERTIFIED.
            </h2>
            <h3 className="heading-3 text-block-2" style={{ fontWeight: 500 }}>
              Cert #: {certificateNumber ?? "PENDING"}
            </h3>
            <a
              className="navbar-button cert-download-btn"
              href={`/certificate/${certificateNumber}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              View Certificate
            </a>

            <button
              className="navbar-button cert-download-btn badge-btn"
              onClick={downloadBadge}
            >
              Download Badge
            </button>

            <div className="social-share" style={{ marginTop: "1em" }}>
              <FacebookShareButton
                url={`${APP_URL}`}
                quote="My company was just certified as a Black-Owned Business by U.S. Black Chambers, Inc. and ByBlack Platform. Go to www.usblackchambers.org/certification to start your application!"
              >
                <FacebookIcon size={32} />
              </FacebookShareButton>

              <TwitterShareButton
                url={`${APP_URL}`}
                title="My company was just certified as a Black-Owned Business by U.S. Black Chambers, Inc. and ByBlack Platform. Go to www.usblackchambers.org/certification to start your application!"
                via={`${APP_URL}`}
              >
                <TwitterIcon size={32} />
              </TwitterShareButton>

              <LinkedinShareButton
                url={`${APP_URL}`}
                summary="My company was just certified as a Black-Owned Business by U.S. Black Chambers, Inc. and ByBlack Platform. Go to www.usblackchambers.org/certification to start your application!"
                source={`${APP_URL}`}
                title="ByBlack Certification"
              >
                <LinkedinIcon size={32} />
              </LinkedinShareButton>
            </div>
          </div>

          <div
            className="block-section-35"
            style={{ margin: "auto", marginTop: -30 }}
          >
            <img
              style={{ maxWidth: 400 }}
              src="/assets/images/badge-new.png"
              alt=""
            />
          </div>
        </div>
        <div className="decoration-container">
          <img
            src="/landing-page/images/grid-dark.svg"
            alt=""
            className="decoration-grid"
          />
          <div
            data-w-id="35f9b2b5-6f2e-bcc0-b419-0ce254d19861"
            className="decoration-circle bg-primary-3"
          />
        </div>
      </div>

      <div className="section-bg">
        <div
          className="block-section container cert-container"
          style={{ marginTop: -8, alignItems: "center" }}
        >
          <div className="block-section-60">
            <div className=" font-weight-bold hero">
              <h4
                className="heading-3 text-block-2 "
                style={{ fontWeight: 600 }}
              >
                Now Join the ByBlack Directory.
              </h4>
              <h5 className="heading-3 text-block-2 ">
                Be discovered or find other black-owned business with a few easy
                steps.
              </h5>
            </div>
          </div>
          <div className="block-section-35 ">
            <Link to="/register" className="navbar-button j-button">
              Join Now
            </Link>
          </div>
        </div>
      </div>

      <div className="section bg-gray-4">
        <div className="container">
          <div className="content-row align-center">
            <div
              data-w-id="fcfb1eb7-ad26-f467-f92a-3e277e556122"
              className="content-row-image-wrapper"
            >
              <img
                src="/assets/images/landing-page/GettyImages-629567166.jpg"
                width={486}
                srcSet="/assets/images/landing-page/GettyImages-629567166-p-500.jpeg 500w, /assets/images/landing-page/GettyImages-629567166-p-800.jpeg 800w, /assets/images/landing-page/GettyImages-629567166-p-1080.jpeg 1080w, /assets/images/landing-page/GettyImages-629567166.jpg 1233w"
                sizes="(max-width: 479px) 100vw, 486px"
                alt=""
                className="content-row-image"
              />
              <div
                data-w-id="fcfb1eb7-ad26-f467-f92a-3e277e556124"
                className="decoration-circle content-row-image-circle bg-primary-3"
              />
            </div>
            <div
              data-w-id="fcfb1eb7-ad26-f467-f92a-3e277e55610f"
              className="content-row-contents"
            >
              <h3 className="page-progress">Building Relationships.</h3>
              <h5 className="text-block-2 ">
                More certification benefits to come so look out for email
                notifications for partnerships & opportunities.
              </h5>
              <Link to="/page/benefits" className="perk-outline-btn">
                Corporate Perks
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="section bg-gray-4" style={{ paddingBottom: 35 }}>
        <div className="container">
          <div className="content-row align-center">
            <div
              data-w-id="fcfb1eb7-ad26-f467-f92a-3e277e55610f"
              className="content-row-contents custom-box-parent"
            >
              <div className="box box1">
                <h3 className="cert-box-title-1">Corporate Perks</h3>
                <h5 className="cert-box-content-1">
                  More certification benefits to come so look out for email
                  notifications for partnerships & opportunities.
                </h5>
                <Link to="/page/benefits" className="cert-box-link-1">
                  View More
                </Link>
              </div>
            </div>

            <div
              data-w-id="fcfb1eb7-ad26-f467-f92a-3e277e55610f"
              className="content-row-contents custom-box-parent"
            >
              <div className="box box2">
                <h3 className="cert-box-title-2">Opportunities</h3>
                <h5 className="cert-box-content-2">
                  More certification benefits to come so look out for email
                  notifications for partnerships & opportunities.
                </h5>
                <a
                  href="https://www.usblackchambers.org/certification "
                  className="cert-box-link-2"
                >
                  View More
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="section bg-gray-4" style={{ paddingTop: 35 }}>
        <div className="container">
          <div className="content-row align-center">
            <div
              data-w-id="fcfb1eb7-ad26-f467-f92a-3e277e55610f"
              className="content-row-contents custom-box-parent"
            >
              <div className="box box3">
                <h3 className="cert-box-title-1">Upcoming Events</h3>
                <h5 className="cert-box-content-1">
                  More certification benefits to come so look out for email
                  notifications for partnerships & opportunities.
                </h5>
                <a
                  href="https://usblackchambers.org/"
                  className="cert-box-link-1"
                >
                  View More
                </a>
              </div>
            </div>

            <div
              data-w-id="fcfb1eb7-ad26-f467-f92a-3e277e55610f"
              className="content-row-contents custom-box-parent"
            >
              <div className="box box4">
                <h3 className="cert-box-title-2">Find a US Black Chamber</h3>
                <h5 className="cert-box-content-2">
                  More certification benefits to come so look out for email
                  notifications for partnerships & opportunities.
                </h5>
                <a
                  href="https://usblackchambers.org/chambers/"
                  className="cert-box-link-2"
                >
                  View More
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer bg-primary">
        <div className="container">
          <Link to="/" className="w-inline-block">
            <img
              src="/assets/images/landing-page/Vector3x.png"
              width={150}
              alt=""
            />
          </Link>
          <div className="footer-content">
            <div className="footer-menus">
              <div className="footer-links-large">
                <Link
                  to="/page/benefits"
                  className="footer-link footer-link-large text-white"
                >
                  Program Benefits
                </Link>
                <Link
                  to="/page/qualifications"
                  className="footer-link footer-link-large text-white"
                >
                  Qualifications
                </Link>
                <Link
                  to="/login"
                  className="footer-link footer-link-large text-white"
                >
                  Status of Application
                </Link>
                <Link
                  to="#faq"
                  className="footer-link footer-link-large text-white"
                >
                  FAQs
                </Link>
              </div>
            </div>
            <div>
              <Link to="#" className="footer-link footer-link-large text-white">
                certification@byblack.us
              </Link>
              <div className="social-links footer-social-links">
                <a
                  href=" https://twitter.com/usblackchambers"
                  className="button button-round social-button-white w-inline-block"
                >
                  <img
                    src="/assets/images/landing-page/twitter-light.svg"
                    alt=""
                    className="social-button-icon"
                  />
                </a>
                <a
                  href="https://www.facebook.com/usblackchambers/"
                  className="button button-round social-button-white w-inline-block"
                >
                  <img
                    src="/assets/images/landing-page/facebook-light.svg"
                    alt=""
                    className="social-button-icon"
                  />
                </a>
              </div>
            </div>
          </div>
          <div>
            <div className="text-small">
              2020 © Copyright • US Black Chamber of Commerces | ByBlack
              <Link
                to={"https://webflow.com/"}
                className="text-white"
                target="_blank"
                rel="noopener noreferrer"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
